import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  MenuItem,
  Chip,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  SearchOutlined,
  PersonAddAlt1Outlined,
  TwoWheelerOutlined,
  KeyOutlined,
  DirectionsCarOutlined,
  AddCircleOutline,
  PedalBikeOutlined,
  LocalShippingOutlined,
  Inventory2Outlined,
  EventOutlined,
  StarOutlined,
} from '@mui/icons-material';
import Layout from '../components/Layout';
import StatusBadge from '../components/StatusBadge';
import EmptyState from '../components/EmptyState';
import LoadingState from '../components/LoadingState';
import { couriersService, companyService } from '../api/services';
import { useLanguage } from '../context/LanguageContext';
import { getApiErrorMessage } from '../utils/errorHandler';

export const getVehicleTypeInfo = (type, isRtl) => {
  const tStr = String(type || '').toLowerCase();
  if (type === 1 || tStr === '1' || tStr.includes('موتوسيكل') || tStr.includes('motorcycle') || tStr.includes('دراجة نارية')) {
    return {
      name: isRtl ? 'موتوسيكل (دراجة نارية)' : 'Motorcycle',
      icon: <TwoWheelerOutlined sx={{ color: '#EF6C00', fontSize: 22 }} />,
      color: '#EF6C00',
    };
  }
  if (type === 2 || tStr === '2' || tStr.includes('سيارة') || tStr.includes('car')) {
    return {
      name: isRtl ? 'سيارة' : 'Car',
      icon: <DirectionsCarOutlined sx={{ color: '#8B5CF6', fontSize: 22 }} />,
      color: '#8B5CF6',
    };
  }
  if (type === 3 || tStr === '3' || tStr.includes('دراجة') || tStr.includes('bike') || tStr.includes('سكوتر') || tStr.includes('scooter')) {
    return {
      name: isRtl ? 'دراجة هوائية / سكوتر' : 'Bicycle / Scooter',
      icon: <PedalBikeOutlined sx={{ color: '#3B82F6', fontSize: 22 }} />,
      color: '#3B82F6',
    };
  }
  if (type === 4 || tStr === '4' || tStr.includes('فان') || tStr.includes('van') || tStr.includes('شاحنة') || tStr.includes('truck')) {
    return {
      name: isRtl ? 'فان / شاحنة خفيفة' : 'Van',
      icon: <LocalShippingOutlined sx={{ color: '#10B981', fontSize: 22 }} />,
      color: '#10B981',
    };
  }
  return {
    name: typeof type === 'string' && type.trim() ? type : (isRtl ? 'مركبة' : 'Vehicle'),
    icon: <DirectionsCarOutlined sx={{ color: '#64748B', fontSize: 22 }} />,
    color: '#64748B',
  };
};

const Couriers = () => {
  const { t, isRtl, dir } = useLanguage();
  const [couriers, setCouriers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  // Button Loading States
  const [submittingCourier, setSubmittingCourier] = useState(false);
  const [submittingAccount, setSubmittingAccount] = useState(false);
  const [submittingVehicle, setSubmittingVehicle] = useState(false);

  // Courier User Modal
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [selectedCourier, setSelectedCourier] = useState(null);
  const [courierAccount, setCourierAccount] = useState(null);
  const [userLoading, setUserLoading] = useState(false);
  const [userForm, setUserForm] = useState({ userName: '', password: '', phone: '' });

  // Courier Vehicles Modal
  const [vehiclesModalOpen, setVehiclesModalOpen] = useState(false);
  const [selectedCourierForVehicles, setSelectedCourierForVehicles] = useState(null);
  const [courierVehicles, setCourierVehicles] = useState([]);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);
  const [newVehicleForm, setNewVehicleForm] = useState({
    vehicleType: 1,
    plateNumber: '',
    capacityOrders: 3,
    licenseExpiresOn: '',
    isPrimary: true,
    model: '',
    color: '',
  });

  // Add Courier Form State based on Swagger Schema
  const [newCourier, setNewCourier] = useState({
    courierCode: '',
    fullNameAr: '',
    fullNameEn: '',
    mobile: '',
    nationalId: '',
    companyBranchId: '',
    courierCategoryId: null,
    creditLimit: 5000,
    maximumConcurrentOrders: 3,
    employmentDate: new Date().toISOString().split('T')[0],
    status: 1,
  });

  const fetchBranches = async () => {
    try {
      const res = await companyService.getBranches();
      const raw = Array.isArray(res) ? res : (res?.items || res?.data || []);
      const items = raw.map((b) => {
        const branchId = b.companyBranchId ?? b.id ?? b.branchId;
        return {
          ...b,
          id: branchId,
          companyBranchId: branchId,
        };
      });
      setBranches(items);
      if (items.length > 0) {
        setNewCourier((prev) => ({
          ...prev,
          companyBranchId: prev.companyBranchId || items[0].companyBranchId,
        }));
      }
    } catch (e) {
      console.warn('Branches API fetch error:', e);
      setBranches([]);
    }
  };

  const fetchCouriers = async () => {
    setLoading(true);
    try {
      const res = await couriersService.getCouriers();
      if (res) {
        const raw = Array.isArray(res) ? res : (res.items || []);
        const list = raw.map((c) => ({
          ...c,
          id: c.courierId ?? c.id,
          courierId: c.courierId ?? c.id,
        }));
        setCouriers(list);
      } else {
        setCouriers([]);
      }
    } catch (e) {
      console.warn('Couriers fetch error:', e);
      setCouriers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCouriers();
    fetchBranches();
  }, []);

  const filteredCouriers = couriers.filter((c) => {
    const name = (c.fullNameAr || c.name || c.fullName || '').toLowerCase();
    const phone = c.mobile || c.phone || '';
    const nid = c.nationalId || '';
    const code = (c.courierCode || '').toLowerCase();
    const query = searchTerm.toLowerCase();

    return name.includes(query) || phone.includes(query) || nid.includes(query) || code.includes(query);
  });

  const handleAddCourier = async (e) => {
    if (e) e.preventDefault();

    const errors = {};
    if (!newCourier.fullNameAr.trim()) {
      errors.fullNameAr = t('requiredField');
    }
    if (!newCourier.mobile.trim()) {
      errors.mobile = t('requiredField');
    }
    if (!newCourier.companyBranchId) {
      errors.companyBranchId = t('requiredField');
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setToast({ open: true, message: t('couriersPage.messages.fillRequired'), severity: 'error' });
      return;
    }

    setFormErrors({});

    const payload = {
      courierCode: newCourier.courierCode.trim() || `CR-${Date.now().toString().slice(-4)}`,
      fullNameAr: newCourier.fullNameAr.trim(),
      fullNameEn: newCourier.fullNameEn.trim() || newCourier.fullNameAr.trim(),
      mobile: newCourier.mobile.trim(),
      nationalId: newCourier.nationalId.trim(),
      companyBranchId: Number(newCourier.companyBranchId) || 1,
      courierCategoryId: null,
      creditLimit: Number(newCourier.creditLimit) || 0,
      maximumConcurrentOrders: Number(newCourier.maximumConcurrentOrders) || 3,
      employmentDate: newCourier.employmentDate || new Date().toISOString().split('T')[0],
      status: Number(newCourier.status) || 1,
    };

    setSubmittingCourier(true);
    try {
      await couriersService.createCourier(payload);
      setToast({ open: true, message: `${t('couriersPage.messages.addSuccess')} (${payload.fullNameAr})`, severity: 'success' });
      setAddModalOpen(false);
      setNewCourier({
        courierCode: '',
        fullNameAr: '',
        fullNameEn: '',
        mobile: '',
        nationalId: '',
        companyBranchId: branches[0]?.companyBranchId || '',
        courierCategoryId: null,
        creditLimit: 5000,
        maximumConcurrentOrders: 3,
        employmentDate: new Date().toISOString().split('T')[0],
        status: 1,
      });
      fetchCouriers();
    } catch (err) {
      setToast({ open: true, message: getApiErrorMessage(err, `${t('couriersPage.messages.addFail')} ${err.message || ''}`), severity: 'error' });
    } finally {
      setSubmittingCourier(false);
    }
  };

  // Courier User Management
  const handleOpenUserModal = async (courier) => {
    setSelectedCourier(courier);
    setUserModalOpen(true);
    setUserLoading(true);
    const cId = courier.courierId ?? courier.id;
    setUserForm({
      userName: courier.courierCode || courier.mobile || courier.phone || '',
      fullName: courier.fullNameAr || courier.name || courier.fullName || '',
      email: courier.email || '',
      mobile: courier.mobile || courier.phone || '',
      password: '',
      newPassword: '',
    });
    try {
      const acc = await couriersService.getCourierUser(cId);
      setCourierAccount(acc);
      if (acc) {
        setUserForm((prev) => ({
          ...prev,
          userName: acc.userName || prev.userName,
          fullName: acc.fullName || prev.fullName,
          email: acc.email || prev.email,
          mobile: acc.mobile || prev.mobile,
        }));
      }
    } catch (e) {
      setCourierAccount(null);
    } finally {
      setUserLoading(false);
    }
  };

  const handleCreateAccount = async (e) => {
    if (e) e.preventDefault();
    if (!selectedCourier) return;
    const cId = selectedCourier.courierId ?? selectedCourier.id;
    if (!userForm.userName.trim() || !userForm.password.trim()) {
      setToast({ open: true, message: isRtl ? 'يرجى إدخال اسم المستخدم وكلمة المرور' : 'Please enter username and password', severity: 'error' });
      return;
    }

    setSubmittingAccount(true);
    try {
      await couriersService.createCourierUser(cId, {
        userName: userForm.userName.trim(),
        fullName: userForm.fullName?.trim() || selectedCourier.fullNameAr || selectedCourier.name || '',
        email: userForm.email?.trim() || '',
        mobile: userForm.mobile?.trim() || selectedCourier.mobile || '',
        password: userForm.password,
        isActive: true,
      });
      setToast({ open: true, message: t('couriersPage.userModal.accountCreatedMsg'), severity: 'success' });
      const acc = await couriersService.getCourierUser(cId);
      setCourierAccount(acc);
    } catch (err) {
      setToast({ open: true, message: getApiErrorMessage(err, `Error: ${err.message || ''}`), severity: 'error' });
    } finally {
      setSubmittingAccount(false);
    }
  };

  const handleResetCourierPassword = async () => {
    if (!selectedCourier || !userForm.newPassword.trim()) {
      setToast({ open: true, message: isRtl ? 'يرجى إدخال كلمة المرور الجديدة' : 'Please enter new password', severity: 'error' });
      return;
    }
    const cId = selectedCourier.courierId ?? selectedCourier.id;
    setSubmittingAccount(true);
    try {
      await couriersService.updateCourierUserPassword(cId, userForm.newPassword.trim());
      setToast({ open: true, message: t('couriersPage.userModal.passwordResetMsg'), severity: 'success' });
      setUserForm((prev) => ({ ...prev, newPassword: '' }));
    } catch (err) {
      setToast({ open: true, message: getApiErrorMessage(err, isRtl ? 'فشل تحديث كلمة المرور' : 'Failed to update password'), severity: 'error' });
    } finally {
      setSubmittingAccount(false);
    }
  };

  const handleToggleUserStatus = async () => {
    if (!selectedCourier || !courierAccount) return;
    const cId = selectedCourier.courierId ?? selectedCourier.id;
    try {
      const newStatus = !courierAccount.isActive;
      await couriersService.updateCourierUserStatus(cId, newStatus);
      setCourierAccount({ ...courierAccount, isActive: newStatus });
      setToast({ open: true, message: `${isRtl ? 'تم تحديث حالة الحساب إلى:' : 'Account status updated to:'} ${newStatus ? t('active') : t('inactive')}`, severity: 'info' });
    } catch (err) {
      setToast({ open: true, message: isRtl ? 'فشل تغيير حالة الحساب' : 'Failed to change account status', severity: 'error' });
    }
  };

  // Courier Vehicles Management
  const handleOpenVehiclesModal = async (courier) => {
    setSelectedCourierForVehicles(courier);
    setVehiclesModalOpen(true);
    setVehiclesLoading(true);
    setNewVehicleForm({
      vehicleType: 1,
      plateNumber: '',
      capacityOrders: 3,
      licenseExpiresOn: '',
      isPrimary: true,
      model: '',
      color: '',
    });
    try {
      const cId = courier.courierId ?? courier.id;
      const res = await couriersService.getCourierVehicles(cId);
      setCourierVehicles(Array.isArray(res) ? res : (res?.items || []));
    } catch (e) {
      console.warn('Vehicles fetch error:', e);
      setCourierVehicles([]);
    } finally {
      setVehiclesLoading(false);
    }
  };

  const handleAddVehicle = async (e) => {
    if (e) e.preventDefault();
    if (!selectedCourierForVehicles) return;
    if (!newVehicleForm.plateNumber.trim()) {
      setToast({ open: true, message: isRtl ? 'يرجى إدخال رقم اللوحة المعدنية' : 'Please enter plate number', severity: 'error' });
      return;
    }

    setSubmittingVehicle(true);
    try {
      const cId = selectedCourierForVehicles.courierId ?? selectedCourierForVehicles.id;
      await couriersService.createVehicle({
        courierId: cId,
        vehicleType: Number(newVehicleForm.vehicleType) || 1,
        plateNumber: newVehicleForm.plateNumber.trim(),
        capacityOrders: Number(newVehicleForm.capacityOrders) || 3,
        licenseExpiresOn: newVehicleForm.licenseExpiresOn || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        isPrimary: Boolean(newVehicleForm.isPrimary),
        isActive: true,
        model: newVehicleForm.model?.trim() || '',
        color: newVehicleForm.color?.trim() || '',
      });
      setToast({ open: true, message: t('couriersPage.vehiclesModal.addedSuccess'), severity: 'success' });
      setNewVehicleForm({
        vehicleType: 1,
        plateNumber: '',
        capacityOrders: 3,
        licenseExpiresOn: '',
        isPrimary: false,
        model: '',
        color: '',
      });
      const res = await couriersService.getCourierVehicles(cId);
      setCourierVehicles(Array.isArray(res) ? res : (res?.items || []));
    } catch (err) {
      setToast({ open: true, message: getApiErrorMessage(err, `Error: ${err.message || ''}`), severity: 'error' });
    } finally {
      setSubmittingVehicle(false);
    }
  };

  return (
    <Layout>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5, flexWrap: 'wrap', gap: 1.5 }}>
        <Box sx={{ textAlign: isRtl ? 'right' : 'left' }}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary', mb: 0.2 }}>
            {t('couriersPage.title')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('couriersPage.subtitle')}
          </Typography>
        </Box>

        <Button
          variant="contained"
          size="small"
          startIcon={<PersonAddAlt1Outlined sx={{ fontSize: 16 }} />}
          onClick={() => {
            fetchBranches();
            setFormErrors({});
            setAddModalOpen(true);
          }}
          sx={{
            borderRadius: '8px',
            fontSize: '0.8rem',
            py: 0.6,
            background: 'linear-gradient(135deg, #EF6C00 0%, #E65100 100%)',
          }}
        >
          {t('couriersPage.addCourierBtn')}
        </Button>
      </Box>

      {/* Search & Filter */}
      <Card sx={{ mb: 2.5 }}>
        <CardContent sx={{ p: 2 }}>
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                size="small"
                placeholder={t('couriersPage.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlined sx={{ color: 'text.secondary', fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Chip
                label={`${t('couriersPage.totalCouriers')}: ${couriers.length}`}
                sx={{ fontWeight: 700, height: 38, width: '100%', fontSize: '0.8rem', bgcolor: 'action.hover' }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Couriers Table */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <LoadingState />
          ) : filteredCouriers.length === 0 ? (
            <EmptyState message={t('noData')} subtitle={isRtl ? 'يمكنك إضافة كابتن جديد من الزر أعلاه' : 'You can add a new courier using the button above'} />
          ) : (
            <TableContainer>
              <Table size="small" sx={{ minWidth: 850 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', width: 220 }}>{t('couriersPage.tableHeaders.courier')}</TableCell>
                    <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', width: 140 }}>{isRtl ? 'الفرع' : 'Branch'}</TableCell>
                    <TableCell sx={{ textAlign: 'center !important', width: 110 }}>{t('couriersPage.tableHeaders.status')}</TableCell>
                    <TableCell sx={{ textAlign: 'center !important', width: 110 }}>{t('verificationStatus')}</TableCell>
                    <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', width: 120 }}>{t('cashInHand')}</TableCell>
                    <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', width: 100 }}>{t('completedOrders')}</TableCell>
                    <TableCell sx={{ textAlign: 'center !important', width: 170 }}>{t('couriersPage.tableHeaders.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredCouriers.map((courier) => (
                    <TableRow key={courier.id} hover>
                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', whiteSpace: 'nowrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.dark', fontWeight: 700, width: 30, height: 30, fontSize: '0.75rem' }}>
                            {(courier.fullNameAr || courier.name || courier.fullName || 'C').charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                              {courier.fullNameAr || courier.name || courier.fullName}
                              {courier.courierCode && (
                                <Chip label={courier.courierCode} size="small" sx={{ ml: 0.5, mr: 0.5, height: 18, fontSize: '0.65rem', fontWeight: 700 }} />
                              )}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.68rem' }}>
                              {courier.mobile || courier.phone} {courier.nationalId ? `• ${courier.nationalId}` : ''}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" sx={{ fontSize: '0.78rem' }}>
                          {courier.branchName || (branches.find((b) => b.id === courier.companyBranchId)?.name) || (isRtl ? 'الفرع الرئيسي' : 'Main Branch')}
                        </Typography>
                      </TableCell>

                      <TableCell align="center" sx={{ textAlign: 'center !important' }}>
                        <StatusBadge status={courier.status === 1 ? 'Active' : (courier.status === 2 ? 'Busy' : 'Offline')} size="small" />
                      </TableCell>

                      <TableCell align="center" sx={{ textAlign: 'center !important' }}>
                        <StatusBadge status={courier.verificationStatus || 'Verified'} size="small" />
                      </TableCell>

                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.8rem' }}>
                          {courier.cashInHand || courier.creditLimit || '0.00'} {t('currency')}
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main', fontSize: '0.8rem' }}>
                          {courier.completedOrders || 0} {isRtl ? 'طلب' : 'orders'}
                        </Typography>
                      </TableCell>

                      <TableCell align="center" sx={{ textAlign: 'center !important', whiteSpace: 'nowrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.8 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<TwoWheelerOutlined sx={{ fontSize: 14 }} />}
                            onClick={() => handleOpenVehiclesModal(courier)}
                            sx={{ borderRadius: '6px', fontSize: '0.72rem', py: 0.3, px: 0.8 }}
                          >
                            {isRtl ? 'المركبات' : 'Vehicles'}
                          </Button>

                          <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            startIcon={<KeyOutlined sx={{ fontSize: 14 }} />}
                            onClick={() => handleOpenUserModal(courier)}
                            sx={{ borderRadius: '6px', fontSize: '0.72rem', py: 0.3, px: 0.8 }}
                          >
                            {isRtl ? 'الحساب' : 'Account'}
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Add Courier Modal matching exact Swagger Schema */}
      <Dialog
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        maxWidth="md"
        fullWidth
        dir={dir}
        PaperProps={{ sx: { borderRadius: '14px', p: 1 }, dir: dir, style: { direction: dir } }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1rem', pb: 1, textAlign: isRtl ? 'right' : 'left' }}>
          {t('couriersPage.addModal.title')}
        </DialogTitle>
        <DialogContent dir={dir} style={{ direction: dir }}>
          <Box component="form" noValidate dir={dir} style={{ direction: dir }} onSubmit={handleAddCourier} sx={{ display: 'flex', flexDirection: 'column', gap: 1.8, mt: 1 }}>
            <Grid container spacing={1.5} dir={dir} style={{ direction: dir }}>
              {/* Row 1: Code & Arabic Name */}
              <Grid item xs={12} sm={4}>
                <TextField
                  label={t('couriersPage.addModal.courierCode')}
                  fullWidth
                  size="small"
                  value={newCourier.courierCode}
                  onChange={(e) => setNewCourier({ ...newCourier, courierCode: e.target.value })}
                  placeholder="e.g. CR-101"
                />
              </Grid>

              <Grid item xs={12} sm={8}>
                <TextField
                  label={t('couriersPage.addModal.fullNameAr')}
                  fullWidth
                  size="small"
                  value={newCourier.fullNameAr}
                  onChange={(e) => {
                    setNewCourier({ ...newCourier, fullNameAr: e.target.value });
                    if (formErrors.fullNameAr) setFormErrors({ ...formErrors, fullNameAr: '' });
                  }}
                  error={Boolean(formErrors.fullNameAr)}
                  helperText={formErrors.fullNameAr}
                  required
                />
              </Grid>

              {/* Row 2: English Name & Mobile */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('couriersPage.addModal.fullNameEn')}
                  fullWidth
                  size="small"
                  value={newCourier.fullNameEn}
                  onChange={(e) => setNewCourier({ ...newCourier, fullNameEn: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('couriersPage.addModal.mobile')}
                  fullWidth
                  size="small"
                  value={newCourier.mobile}
                  onChange={(e) => {
                    setNewCourier({ ...newCourier, mobile: e.target.value });
                    if (formErrors.mobile) setFormErrors({ ...formErrors, mobile: '' });
                  }}
                  error={Boolean(formErrors.mobile)}
                  helperText={formErrors.mobile}
                  required
                  placeholder="01xxxxxxxxx"
                />
              </Grid>

              {/* Row 3: National ID & Branch */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('couriersPage.addModal.nationalId')}
                  fullWidth
                  size="small"
                  value={newCourier.nationalId}
                  onChange={(e) => setNewCourier({ ...newCourier, nationalId: e.target.value })}
                  placeholder="14 digits"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  size="small"
                  label={t('couriersPage.addModal.companyBranchId')}
                  fullWidth
                  value={newCourier.companyBranchId || ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    setNewCourier({ ...newCourier, companyBranchId: val });
                    if (formErrors.companyBranchId) setFormErrors({ ...formErrors, companyBranchId: '' });
                  }}
                  error={Boolean(formErrors.companyBranchId)}
                  helperText={formErrors.companyBranchId}
                  required
                >
                  {branches.length === 0 ? (
                    <MenuItem value="" disabled>
                      {isRtl ? 'لا توجد فروع مسجلة بالخادم' : 'No branches found on server'}
                    </MenuItem>
                  ) : (
                    branches.map((b) => {
                      const bId = b.companyBranchId ?? b.id;
                      return (
                        <MenuItem key={bId} value={bId}>
                          {b.branchNameAr || b.branchNameEn || (b.branchCode ? `فرع (${b.branchCode})` : `فرع #${bId}`)}
                        </MenuItem>
                      );
                    })
                  )}
                </TextField>
              </Grid>

              {/* Row 4: Credit Limit & Max Concurrent Orders */}
              <Grid item xs={12} sm={6}>
                <TextField
                  type="number"
                  label={t('couriersPage.addModal.creditLimit')}
                  fullWidth
                  size="small"
                  value={newCourier.creditLimit}
                  onChange={(e) => setNewCourier({ ...newCourier, creditLimit: e.target.value })}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">{t('currency')}</InputAdornment>,
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  type="number"
                  label={t('couriersPage.addModal.maximumConcurrentOrders')}
                  fullWidth
                  size="small"
                  value={newCourier.maximumConcurrentOrders}
                  onChange={(e) => setNewCourier({ ...newCourier, maximumConcurrentOrders: e.target.value })}
                />
              </Grid>

              {/* Row 5: Employment Date */}
              <Grid item xs={12}>
                <TextField
                  type="date"
                  label={t('couriersPage.addModal.employmentDate')}
                  fullWidth
                  size="small"
                  value={newCourier.employmentDate}
                  onChange={(e) => setNewCourier({ ...newCourier, employmentDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            <DialogActions sx={{ px: 0, pt: 2, direction: dir }}>
              <Button onClick={() => setAddModalOpen(false)} color="inherit" size="small">
                {t('cancel')}
              </Button>
              <Button
                type="submit"
                variant="contained"
                size="small"
                disabled={submittingCourier}
                startIcon={submittingCourier ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : null}
                sx={{
                  background: 'linear-gradient(135deg, #EF6C00 0%, #E65100 100%)',
                  fontWeight: 700,
                  px: 2.5,
                }}
              >
                {submittingCourier ? (isRtl ? 'جاري الحفظ...' : 'Saving...') : t('couriersPage.addModal.saveBtn')}
              </Button>
            </DialogActions>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Courier Vehicles Modal */}
      <Dialog
        open={vehiclesModalOpen}
        onClose={() => setVehiclesModalOpen(false)}
        maxWidth="md"
        fullWidth
        dir={dir}
        PaperProps={{ sx: { borderRadius: '14px', p: 1 }, dir: dir, style: { direction: dir } }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1rem', textAlign: isRtl ? 'right' : 'left' }}>
          {t('couriersPage.vehiclesModal.title')}
        </DialogTitle>
        <DialogContent dir={dir} style={{ direction: dir }}>
          {selectedCourierForVehicles && (
            <Box dir={dir} style={{ direction: dir }} sx={{ my: 1, textAlign: isRtl ? 'right' : 'left' }}>
              <Box sx={{ p: 1.5, borderRadius: '8px', bgcolor: 'action.hover', mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 800 }}>
                    {t('couriersPage.vehiclesModal.courierLabel')} {selectedCourierForVehicles.fullNameAr || selectedCourierForVehicles.name || selectedCourierForVehicles.fullName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selectedCourierForVehicles.mobile || selectedCourierForVehicles.phone}
                  </Typography>
                </Box>
                <Chip
                  label={`${courierVehicles.length} ${isRtl ? 'مركبة مسجلة' : 'Vehicles'}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ fontWeight: 700 }}
                />
              </Box>

              {/* Add New Vehicle Form */}
              <Box component="form" onSubmit={handleAddVehicle} sx={{ mb: 2.5, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: '12px', bgcolor: 'background.paper' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, display: 'block', mb: 1.5, color: 'primary.main' }}>
                  {t('couriersPage.vehiclesModal.addVehicleTitle')}
                </Typography>

                <Grid container spacing={1.5} dir={dir} style={{ direction: dir }}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      select
                      size="small"
                      label={t('couriersPage.vehiclesModal.vehicleTypeLabel')}
                      fullWidth
                      value={newVehicleForm.vehicleType}
                      onChange={(e) => setNewVehicleForm({ ...newVehicleForm, vehicleType: Number(e.target.value) })}
                      required
                    >
                      <MenuItem value={1}>{isRtl ? 'موتوسيكل (دراجة نارية)' : 'Motorcycle'}</MenuItem>
                      <MenuItem value={2}>{isRtl ? 'سيارة' : 'Car'}</MenuItem>
                      <MenuItem value={3}>{isRtl ? 'دراجة هوائية / سكوتر' : 'Bicycle / Scooter'}</MenuItem>
                      <MenuItem value={4}>{isRtl ? 'فان / شاحنة خفيفة' : 'Van'}</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      label={t('couriersPage.vehiclesModal.plateNumberLabel')}
                      fullWidth
                      size="small"
                      value={newVehicleForm.plateNumber}
                      onChange={(e) => setNewVehicleForm({ ...newVehicleForm, plateNumber: e.target.value })}
                      placeholder={isRtl ? 'مثال: ك د م 8327' : 'e.g. ABC 1234'}
                      required
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      label={t('couriersPage.vehiclesModal.capacityOrdersLabel')}
                      type="number"
                      fullWidth
                      size="small"
                      value={newVehicleForm.capacityOrders}
                      onChange={(e) => setNewVehicleForm({ ...newVehicleForm, capacityOrders: Number(e.target.value) || 1 })}
                      inputProps={{ min: 1, max: 20 }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={t('couriersPage.vehiclesModal.licenseExpiresOnLabel')}
                      type="date"
                      fullWidth
                      size="small"
                      value={newVehicleForm.licenseExpiresOn}
                      onChange={(e) => setNewVehicleForm({ ...newVehicleForm, licenseExpiresOn: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      size="small"
                      label={t('couriersPage.vehiclesModal.isPrimaryLabel')}
                      fullWidth
                      value={newVehicleForm.isPrimary ? 'true' : 'false'}
                      onChange={(e) => setNewVehicleForm({ ...newVehicleForm, isPrimary: e.target.value === 'true' })}
                    >
                      <MenuItem value="true">{isRtl ? 'نعم (مركبة أساسية)' : 'Yes (Primary Vehicle)'}</MenuItem>
                      <MenuItem value="false">{isRtl ? 'لا (مركبة إضافية)' : 'No (Secondary Vehicle)'}</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="small"
                      disabled={submittingVehicle}
                      startIcon={submittingVehicle ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <AddCircleOutline />}
                      fullWidth
                      sx={{
                        borderRadius: '8px',
                        background: 'linear-gradient(135deg, #EF6C00 0%, #E65100 100%)',
                        fontWeight: 700,
                        py: 0.9,
                      }}
                    >
                      {submittingVehicle ? (isRtl ? 'جاري الإضافة...' : 'Adding...') : t('couriersPage.vehiclesModal.addBtn')}
                    </Button>
                  </Grid>
                </Grid>
              </Box>

              {/* Current Vehicles List */}
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                {t('couriersPage.vehiclesModal.currentVehicles')}
              </Typography>

              {vehiclesLoading ? (
                <LoadingState message={t('loading')} />
              ) : courierVehicles.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ display: 'block', textAlign: 'center', py: 3, bgcolor: 'action.hover', borderRadius: '8px' }}>
                  {t('couriersPage.vehiclesModal.noVehicles')}
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {courierVehicles.map((veh, idx) => {
                    const typeInfo = getVehicleTypeInfo(veh.vehicleType, isRtl);
                    const vId = veh.courierVehicleId || veh.id || idx;
                    const isPrimary = veh.isPrimary === true;
                    const isActive = veh.isActive !== false;
                    return (
                      <Box
                        key={vId}
                        sx={{
                          p: 1.8,
                          borderRadius: '12px',
                          border: '1px solid',
                          borderColor: isPrimary ? 'primary.main' : 'divider',
                          bgcolor: isPrimary ? 'rgba(239, 108, 0, 0.03)' : 'background.paper',
                          boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1.2,
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                            borderColor: 'primary.main',
                          },
                        }}
                      >
                        {/* Header Row */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: '10px',
                                bgcolor: `${typeInfo.color}18`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              {typeInfo.icon}
                            </Box>
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary' }}>
                                  {typeInfo.name}
                                </Typography>
                                <Chip
                                  label={`#${veh.courierVehicleId || veh.id || idx + 1}`}
                                  size="small"
                                  sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700, bgcolor: 'action.hover' }}
                                />
                              </Box>

                              {veh.plateNumber && (
                                <Box sx={{ mt: 0.4 }}>
                                  <Chip
                                    label={veh.plateNumber}
                                    size="small"
                                    sx={{
                                      fontWeight: 800,
                                      fontFamily: 'monospace',
                                      fontSize: '0.85rem',
                                      letterSpacing: 1,
                                      bgcolor: '#F8FAFC',
                                      color: '#0F172A',
                                      border: '1px solid #CBD5E1',
                                      borderRadius: '6px',
                                      height: 24,
                                    }}
                                  />
                                </Box>
                              )}
                            </Box>
                          </Box>

                          {/* Badges */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
                            {isPrimary && (
                              <Chip
                                icon={<StarOutlined sx={{ fontSize: '15px !important', color: '#F59E0B !important' }} />}
                                label={t('couriersPage.vehiclesModal.primary')}
                                size="small"
                                sx={{
                                  fontWeight: 700,
                                  height: 24,
                                  bgcolor: 'rgba(245, 158, 11, 0.12)',
                                  color: '#B45309',
                                  border: '1px solid rgba(245, 158, 11, 0.3)',
                                }}
                              />
                            )}
                            <Chip
                              label={isActive ? t('couriersPage.vehiclesModal.active') : t('couriersPage.vehiclesModal.inactive')}
                              color={isActive ? 'success' : 'default'}
                              size="small"
                              sx={{ fontWeight: 700, height: 24 }}
                            />
                          </Box>
                        </Box>

                        {/* Details Grid */}
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                            gap: 1.2,
                            pt: 1.2,
                            borderTop: '1px dashed',
                            borderColor: 'divider',
                          }}
                        >
                          {/* Capacity Orders */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                            <Inventory2Outlined sx={{ fontSize: 18, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                              {t('couriersPage.vehiclesModal.capacityOrdersLabel')}:
                            </Typography>
                            <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.primary' }}>
                              {veh.capacityOrders != null ? `${veh.capacityOrders} ${t('couriersPage.vehiclesModal.ordersUnit')}` : (isRtl ? 'غير محدد' : 'N/A')}
                            </Typography>
                          </Box>

                          {/* License Expiry */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                            <EventOutlined sx={{ fontSize: 18, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                              {t('couriersPage.vehiclesModal.expiresOn')}:
                            </Typography>
                            <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.primary' }}>
                              {veh.licenseExpiresOn ? veh.licenseExpiresOn.split('T')[0] : (isRtl ? 'غير محدد' : 'N/A')}
                            </Typography>
                          </Box>

                          {/* Model & Color if present */}
                          {(veh.model || veh.color) && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, gridColumn: { sm: 'span 2' } }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                {isRtl ? 'الموديل واللون:' : 'Model & Color:'}
                              </Typography>
                              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                {veh.model || ''} {veh.color ? `(${veh.color})` : ''}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, direction: dir }}>
          <Button onClick={() => setVehiclesModalOpen(false)} color="inherit" size="small">
            {t('close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Courier App User Account Modal */}
      <Dialog
        open={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        maxWidth="sm"
        fullWidth
        dir={dir}
        PaperProps={{ sx: { borderRadius: '14px', p: 1 }, dir: dir, style: { direction: dir } }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1rem', textAlign: isRtl ? 'right' : 'left' }}>
          {t('couriersPage.userModal.title')}
        </DialogTitle>
        <DialogContent dir={dir} style={{ direction: dir }}>
          {selectedCourier && (
            <Box dir={dir} style={{ direction: dir }} sx={{ my: 1, textAlign: isRtl ? 'right' : 'left' }}>
              <Box sx={{ p: 1.5, borderRadius: '8px', bgcolor: 'action.hover', mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 800 }}>
                    {t('couriersPage.userModal.courierLabel')} {selectedCourier.fullNameAr || selectedCourier.name || selectedCourier.fullName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selectedCourier.mobile || selectedCourier.phone}
                  </Typography>
                </Box>
                {courierAccount && (
                  <Chip
                    label={courierAccount.isActive ? t('active') : t('inactive')}
                    color={courierAccount.isActive ? 'success' : 'default'}
                    size="small"
                    sx={{ fontWeight: 700 }}
                  />
                )}
              </Box>

              {userLoading ? (
                <LoadingState message={t('loading')} />
              ) : courierAccount ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Account Details Box */}
                  <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: '10px', bgcolor: 'background.paper' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.5, color: 'primary.main' }}>
                      {isRtl ? 'بيانات حساب المندوب بالتطبيق:' : 'Courier App Account Details:'}
                    </Typography>

                    <Grid container spacing={1.5}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
                          {isRtl ? 'اسم المستخدم (Username)' : 'Username'}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 800 }}>
                          @{courierAccount.userName || '-'}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
                          {isRtl ? 'الاسم بالكامل' : 'Full Name'}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {courierAccount.fullName || '-'}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
                          {isRtl ? 'رقم الهاتف' : 'Mobile'}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {courierAccount.mobile || '-'}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
                          {isRtl ? 'البريد الإلكتروني' : 'Email'}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {courierAccount.email || '-'}
                        </Typography>
                      </Grid>

                      {courierAccount.userId != null && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
                            {isRtl ? 'معرف المستخدم (User ID)' : 'User ID'}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>
                            #{courierAccount.userId}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>

                    <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Button
                        variant="outlined"
                        size="small"
                        color={courierAccount.isActive ? 'warning' : 'success'}
                        onClick={handleToggleUserStatus}
                        sx={{ fontWeight: 700 }}
                      >
                        {courierAccount.isActive ? (isRtl ? 'إيقاف تفعيل الحساب' : 'Deactivate Account') : (isRtl ? 'تفعيل الحساب' : 'Activate Account')}
                      </Button>
                    </Box>
                  </Box>

                  {/* Reset Password Form */}
                  <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: '10px', bgcolor: 'background.paper' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1, color: 'text.primary' }}>
                      {t('couriersPage.userModal.passwordTitle')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                      <TextField
                        label={t('couriersPage.userModal.newPasswordLabel')}
                        type="password"
                        size="small"
                        sx={{ flex: 1, minWidth: 200 }}
                        value={userForm.newPassword}
                        onChange={(e) => setUserForm({ ...userForm, newPassword: e.target.value })}
                        placeholder={isRtl ? 'أدخل كلمة المرور الجديدة' : 'Enter new password'}
                      />
                      <Button
                        variant="contained"
                        size="small"
                        disabled={submittingAccount || !userForm.newPassword.trim()}
                        startIcon={submittingAccount ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <KeyOutlined />}
                        onClick={handleResetCourierPassword}
                        sx={{
                          background: 'linear-gradient(135deg, #EF6C00 0%, #E65100 100%)',
                          fontWeight: 700,
                          py: 0.9,
                          px: 2,
                        }}
                      >
                        {submittingAccount ? (isRtl ? 'جاري التحديث...' : 'Updating...') : t('couriersPage.userModal.resetBtn')}
                      </Button>
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Box component="form" onSubmit={handleCreateAccount} sx={{ display: 'flex', flexDirection: 'column', gap: 1.8 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {isRtl
                      ? 'لم يتم إنشاء حساب تطبيق لهذا المندوب بعد. أدخل بيانات الدخول لتمكينه من فتح تطبيق المناديب:'
                      : 'No app account created yet for this courier. Set login credentials below:'}
                  </Typography>

                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label={isRtl ? 'اسم المستخدم (Username) *' : 'Username *'}
                        fullWidth
                        size="small"
                        value={userForm.userName}
                        onChange={(e) => setUserForm({ ...userForm, userName: e.target.value })}
                        required
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        label={isRtl ? 'كلمة المرور الأولية *' : 'Initial Password *'}
                        type="password"
                        fullWidth
                        size="small"
                        value={userForm.password}
                        onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                        required
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        label={isRtl ? 'الاسم الكامل' : 'Full Name'}
                        fullWidth
                        size="small"
                        value={userForm.fullName}
                        onChange={(e) => setUserForm({ ...userForm, fullName: e.target.value })}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        label={isRtl ? 'رقم الهاتف' : 'Mobile'}
                        fullWidth
                        size="small"
                        value={userForm.mobile}
                        onChange={(e) => setUserForm({ ...userForm, mobile: e.target.value })}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        label={isRtl ? 'البريد الإلكتروني (اختياري)' : 'Email (Optional)'}
                        fullWidth
                        size="small"
                        value={userForm.email}
                        onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                      />
                    </Grid>
                  </Grid>

                  <Button
                    type="submit"
                    variant="contained"
                    size="small"
                    disabled={submittingAccount}
                    startIcon={submittingAccount ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : null}
                    sx={{
                      background: 'linear-gradient(135deg, #EF6C00 0%, #E65100 100%)',
                      fontWeight: 700,
                      py: 0.9,
                      mt: 1,
                    }}
                  >
                    {submittingAccount ? (isRtl ? 'جاري الإنشاء...' : 'Creating...') : t('couriersPage.userModal.createBtn')}
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, direction: dir }}>
          <Button onClick={() => setUserModalOpen(false)} color="inherit" size="small">
            {t('close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: isRtl ? 'left' : 'right' }}
      >
        <Alert severity={toast.severity} sx={{ borderRadius: '8px', fontWeight: 600 }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
};

export default Couriers;
